import React, { useCallback, useMemo, useState } from 'react';
import cx from 'classnames';

import { NotificationManager } from '@atlas/core';
import { parseWithdrawalQuery } from '@atlas/routing';
import { Button, DefaultPopup, Input, Label, Select } from '@atlas/ui';

import PaymentListItem from 'src/components/ClientProfile/Payment/PaymentListItem';

import { showWithdrawalError, submitWithdrawal } from './withdrawalUtils';

const validateAccountNumber = value => {
  const maxLength = 32;
  const minLength = 1;
  if (!value) return { message: 'required' };

  if (value.length < minLength) {
    return { message: 'min_length', params: { ruleOption: minLength } };
  } else if (value.length > maxLength) {
    return { message: 'max_length', params: { ruleOption: maxLength } };
  } else {
    return null;
  }
};

const WithdrawalMagnetarPopup = ({ data = {}, id }) => {
  const { paymentMethod, pmId, publicPaymentId, redirectUrl } = data;
  const [isActiveInput, setIsActiveInput] = useState(false);
  const [isActiveSelect, setIsActiveSelect] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { amount, currency, question1, question2, question3, question4, question5 } =
    parseWithdrawalQuery(redirectUrl);

  const questions = useMemo(
    () => [
      { text: 'withdrawal_magnetar_question_one', value: question1 },
      { text: 'withdrawal_magnetar_question_two', value: question2 },
      { text: 'withdrawal_magnetar_question_three', value: question3 },
      { text: 'withdrawal_magnetar_question_four', value: question4 },
      { text: 'withdrawal_magnetar_question_five', value: question5 },
    ],
    [question1, question2, question3, question4, question5],
  );

  const [questionValue, setQuestionValue] = useState(questions[0].value);
  const [answerValue, setAnswerValue] = useState('');

  const handleClose = useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const changeAnswerValue = useCallback(event => {
    const value = event.target.value;

    const error = validateAccountNumber(value);

    setError(error);
    setAnswerValue(value);
  }, []);

  const handleClickButton = useCallback(async () => {
    const requestData = {
      additionalParameters: {
        publicPaymentId: publicPaymentId,
        securityAnswer: answerValue,
        securityQuestion: questionValue,
      },
      amount: amount,
      currency: currency,
      pmId: pmId,
    };

    setIsSubmitting(true);

    try {
      await submitWithdrawal({ amount, currency, paymentMethod, requestData });
    } catch {
      showWithdrawalError();
    } finally {
      setIsSubmitting(false);
      handleClose();
    }
  }, [
    publicPaymentId,
    answerValue,
    questionValue,
    amount,
    currency,
    pmId,
    paymentMethod,
    handleClose,
  ]);

  return (
    <DefaultPopup
      className={cx('payment-popup', 'full-height')}
      headerTitle="withdrawal"
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <div className="payment-popup-content">
        <div className="payment-popup-payment-method">
          <PaymentListItem amount={amount} payment={paymentMethod} showAmount />
        </div>

        <Select
          active={isActiveSelect}
          label="withdrawal_magnetar_question"
          onBlur={() => setIsActiveSelect(false)}
          onChange={setQuestionValue}
          onFocus={() => setIsActiveSelect(true)}
          options={questions}
          translateOptions={true}
        />
        <div className={cx('payment-popup-block', 'payment-popup-content-item')}>
          <Input
            active={isActiveInput}
            className="popup-input"
            label="withdrawal_magnetar_answer"
            onBlur={() => setIsActiveInput(false)}
            onChange={changeAnswerValue}
            onFocus={() => setIsActiveInput(true)}
            value={answerValue}
          />
          {error && (
            <div className="help-text-container">
              <Label message={error.message} params={error.params} />
            </div>
          )}
        </div>
      </div>

      <div className="popup__footer">
        <Button
          className="primary big block"
          disabled={!answerValue || isSubmitting || error}
          error={error}
          label="withdrawal"
          loading={isSubmitting}
          onClick={handleClickButton}
        />
      </div>
    </DefaultPopup>
  );
};

export default WithdrawalMagnetarPopup;
